import { DataSet, ReconciliationSettings } from '../types';
import { encrypt, decrypt } from './crypto';

const DB_NAME = 'GSTRReconcilerDB';
const STORE_NAME = 'datasets';
const DB_VERSION = 1;

// Helper to open DB
const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
};

// --- Electron API Bridge ---
const isElectron = window.electronAPI && window.electronAPI.ipcRenderer;

const callElectron = async (channel: string, ...args: any[]): Promise<any> => {
  if (!isElectron) {
    throw new Error("Electron API not available. Cannot perform file system operation.");
  }
  const result = await window.electronAPI!.ipcRenderer.invoke(channel, ...args);
  if (!result.success) {
    throw new Error(result.error);
  }
  return result.data;
};

const getElectronPath = (segments: string[]): string => {
  if (!isElectron) {
    throw new Error("Electron API not available for path operations.");
  }
  return window.electronAPI!.path.join(...segments);
};

// --- Storage Functions ---

const DEFAULT_FILE_NAME = 'data.json';

export const saveLocal = async (key: string, data: DataSet, settings: ReconciliationSettings, encryptionKey?: string | null): Promise<void> => {
  let finalData: any = data;

  // Encrypt if requested and key is available
  if (settings.isEncrypted && encryptionKey && key !== 'app_settings') {
    const encryptedString = encrypt(data, encryptionKey);
    finalData = { _encrypted: true, data: encryptedString };
  }

  // FORCE: app_settings must always be in IndexedDB (local) to avoid circular dependency with storagePath
  if (isElectron && settings.storagePath && key !== 'app_settings') {
    const filePath = getElectronPath([settings.storagePath, key + '.json']);
    await callElectron('write-file', filePath, JSON.stringify(finalData));
    console.log(`Saved data to: ${filePath}`);
  } else {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const request = store.put(finalData, key);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (e) {
      console.error("IndexedDB Save Error", e);
    }
  }
};

export const loadLocal = async (key: string, settings: ReconciliationSettings, encryptionKey?: string | null): Promise<DataSet | null> => {
  let resultData: any = null;

  if (isElectron && settings.storagePath && key !== 'app_settings') {
    const filePath = getElectronPath([settings.storagePath, key + '.json']);
    try {
      const result = await callElectron('read-file', filePath);
      resultData = JSON.parse(result.data);
    } catch (error: any) {
      console.warn(`Could not load from ${filePath}: ${error.message}`);
      return null;
    }
  } else {
    try {
      const db = await openDB();
      resultData = await new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.get(key);

        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
      });
    } catch (e) {
      console.error("IndexedDB Load Error", e);
      return null;
    }
  }

  // Decrypt if necessary
  if (resultData && resultData._encrypted && encryptionKey) {
    try {
      return decrypt(resultData.data, encryptionKey);
    } catch (err) {
      console.error("Decryption failed", err);
      throw new Error("DECRYPTION_FAILED");
    }
  }

  return resultData;
};

export const localBackup = async (filePath: string, data: DataSet): Promise<boolean> => {
  if (!isElectron) {
    alert("Local backup is only available in the desktop application.");
    return false;
  }
  try {
    await callElectron('write-file', filePath, JSON.stringify(data, null, 2));
    return true;
  } catch (error: any) {
    console.error("Local backup failed:", error);
    alert(`Local backup failed: ${error.message}`);
    return false;
  }
};

export const localRestore = async (filePath: string): Promise<DataSet | null> => {
  if (!isElectron) {
    alert("Local restore is only available in the desktop application.");
    return null;
  }
  try {
    const result = await callElectron('read-file', filePath);
    return JSON.parse(result.data);
  } catch (error: any) {
    console.error("Local restore failed:", error);
    alert(`Local restore failed: ${error.message}`);
    return null;
  }
};

export const selectDirectory = async (): Promise<string | null> => {
  if (!isElectron) {
    alert("Directory selection is only available in the desktop application.");
    return null;
  }
  return new Promise((resolve) => {
    window.electronAPI!.ipcRenderer.send('open-directory-dialog');
    window.electronAPI!.ipcRenderer.on('selected-directory', (_event: any, path: string) => {
      resolve(path);
    });
  });
};

// Mock Cloud Service
export const uploadToDrive = async (fileName: string, data: any): Promise<boolean> => {
  console.log(`[Mock] Uploading ${fileName} to Google Drive...`);
  // In a real implementation, you would use Google API client libraries here.
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, 1500);
  });
};

export const downloadFromDrive = async (fileName: string): Promise<any | null> => {
  console.log(`[Mock] Downloading ${fileName} from Google Drive...`);
  // In a real implementation, this would involve Google API client libraries.
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(null);
    }, 1500);
  });
};

export const syncFromGstPortal = async (gstin: string, period: string): Promise<boolean> => {
  console.log(`[Mock] Connecting to GST Portal for ${gstin}...`);
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      reject(new Error("GSP_AUTH_REQUIRED"));
    }, 2000);
  });
};